<template>
  <div class="flex flex-col items-start gap-2 self-stretch">
    <div class="flex w-full items-center justify-between">
      <UITextXsMedium>{{ t("common.columns") }}</UITextXsMedium>
      <UIDropdown id="desktop-mobile-toggle" :options="deviceOptions" @select="switchDeviceMode">
        <template #default>
          <div class="flex items-center gap-0.5">
            <Phone01Icon v-if="store.deviceMode === 'mobile'" class="h-4 w-4 cursor-pointer" />
            <Monitor04Icon v-else class="h-4 w-4 cursor-pointer" />
            <ChevronDownIcon class="h-3 w-3" />
          </div>
        </template>
      </UIDropdown>
    </div>

    <div class="w-full">
      <UISelect
        id="layout-type-select"
        size="large"
        :value="currentLayout"
        :options="layoutOptions"
        @change="handleLayoutChange"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
// Icons removed - using UISelect instead of custom dropdown
import { useAppStore } from "@/store/app";
import { fieldFullWidth } from "@/util/methods";
import { ChevronDownIcon, Monitor04Icon, Phone01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIDropdown, UISelect, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CONSTANTS, deviceOptions, IMAGE_LAYOUT } from "../../../util/constants";
const { t } = useI18n();
const store = useAppStore();

const switchDeviceMode = (option) => {
  store.deviceMode = option;

  // Initialize mobile padding if switching to mobile and it doesn't exist
  if (option === "mobile" && !store.formStyle.mobilePadding) {
    store.formStyle.mobilePadding = { ...store.formStyle.padding };
  }
};

// Layout options for select
const layoutOptions = computed(() => {
  const options = [
    {
      value: 1,
      label: t("common.payment.product.singleColumn"),
    },
  ];

  // Add two column and single line options only for desktop
  if (store.deviceMode !== "mobile") {
    options.push({
      value: 2,
      label: t("common.style.twoColumn"),
    });

    // Only show Single Line option when image layout is topFixed or background (or when no image is shown)
    const currentImageLayout = store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
    const showImage = store.builderConfig.showImage || false;
    const allowSingleLine =
      !showImage ||
      currentImageLayout === IMAGE_LAYOUT.TOP_FIXED ||
      currentImageLayout === IMAGE_LAYOUT.BACKGROUND;

    if (allowSingleLine) {
      options.push({
        value: 3,
        label: t("common.style.singleLine"),
      });
    }
  }

  return options;
});

// Handle layout change from select
const handleLayoutChange = (value: number) => {
  layoutChange(value);
};

const currentLayout = computed(() => {
  if (store.deviceMode === "mobile") {
    // Always use single column layout for mobile
    return 1;
  }

  if (
    [CONSTANTS.ONE_COLUMN, CONSTANTS.TWO_COLUMN, CONSTANTS.SINGLE_LINE].includes(
      store.builderConfig.layout
    )
  ) {
    return store.builderConfig.layout;
  }
  return 1;
});

const layoutChange = (layoutIndex: number) => {
  if (store.deviceMode === "mobile") {
    store.builderConfig.mobileLayout = layoutIndex;
  } else {
    store.builderConfig.layout = layoutIndex;

    if (!store.builderConfig.mobileLayout) {
      store.builderConfig.mobileLayout = layoutIndex;
    }
  }
  applyLayoutToElements(layoutIndex);
};

const applyLayoutToElements = (layoutIndex: number) => {
  const validFieldsForWidth = store.elements.filter(
    (field) => !fieldFullWidth(field.type, true)
  ).length;
  store.elements.forEach((field) => {
    if (!fieldFullWidth(field.type, true)) {
      if (layoutIndex === CONSTANTS.ONE_COLUMN) {
        field.fieldWidthPercentage = 100;
      } else if (layoutIndex === CONSTANTS.TWO_COLUMN) {
        field.fieldWidthPercentage = 50;
      } else {
        field.fieldWidthPercentage = Math.max(
          parseFloat((100 / validFieldsForWidth).toFixed(2)),
          20
        );
        if (field.type === "submit") {
          field.fullwidth = false;
          field.padding.left = 10;
          field.padding.right = 10;
        }
      }
    }
  });
  store.addToHistory();
  store.anyUnsavedChanges = true;
};

// Watch for image layout changes and automatically switch away from Single Line if needed
watch([() => store.builderConfig.desktopImageLayout, () => store.builderConfig.showImage], () => {
  // Only check on desktop mode
  if (store.deviceMode === "mobile") {
    return;
  }

  const currentImageLayout = store.builderConfig.desktopImageLayout || IMAGE_LAYOUT.TOP_FIXED;
  const showImage = store.builderConfig.showImage || false;
  const allowSingleLine =
    !showImage ||
    currentImageLayout === IMAGE_LAYOUT.TOP_FIXED ||
    currentImageLayout === IMAGE_LAYOUT.BACKGROUND;

  // If currently on Single Line layout but it's no longer allowed, switch to Single Column
  if (!allowSingleLine && store.builderConfig.layout === CONSTANTS.SINGLE_LINE) {
    store.builderConfig.layout = CONSTANTS.ONE_COLUMN;
    applyLayoutToElements(CONSTANTS.ONE_COLUMN);
  }
});
</script>

<style scoped>
/* Dropdown styling is handled by UIDropdown component */
</style>
